import * as THREE from 'three';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader.js';
import { CadObject } from '../../../models/cad-object.model';

const BASE_COLOR = new THREE.Color('LightGray');
const NEW_TOPO_COLOR = new THREE.Color('Orange');

/**
 * Geometry attributes that may be uploaded to the GPU as floating-point buffers.
 * PLYLoader r185+ preserves `double` properties as Float64Array, which WebGL
 * cannot render — coerce these to Float32 after load.
 */
const WEBGL_FLOAT_ATTRIBUTES = ['position', 'normal', 'uv', 'color'] as const;

/**
 * Ensures floating-point geometry attributes use Float32Array so WebGL can
 * upload them. Integer attributes (e.g. uchar colors) are left unchanged.
 */
function coerceGeometryAttributesForWebGL(
  geometry: THREE.BufferGeometry,
): void {
  let positionConverted = false;

  for (const name of WEBGL_FLOAT_ATTRIBUTES) {
    const attribute = geometry.getAttribute(name);
    if (!attribute || attribute instanceof THREE.InterleavedBufferAttribute) {
      continue;
    }

    const bufferAttribute = attribute as THREE.BufferAttribute;
    if (bufferAttribute.array instanceof Float32Array) {
      continue;
    }

    // PLY `double` props become Float64Array; WebGL only supports 32-bit floats.
    if (!(bufferAttribute.array instanceof Float64Array)) {
      continue;
    }

    geometry.setAttribute(
      name,
      new THREE.Float32BufferAttribute(
        bufferAttribute.array,
        bufferAttribute.itemSize,
        bufferAttribute.normalized,
      ),
    );

    if (name === 'position') {
      positionConverted = true;
    }
  }

  if (positionConverted) {
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();
  }
}

/** Size in bytes for each PLY scalar type, used when reading binary PLY data. */
const PLY_TYPE_SIZES: Record<string, number> = {
  char: 1,
  uchar: 1,
  int8: 1,
  uint8: 1,
  short: 2,
  ushort: 2,
  int16: 2,
  uint16: 2,
  int: 4,
  uint: 4,
  int32: 4,
  uint32: 4,
  float: 4,
  float32: 4,
  double: 8,
  float64: 8,
};

export async function buildMeshes(
  cadObjects: CadObject[],
  highlightCadObject: CadObject | null = null,
): Promise<void> {
  for (const cadObject of cadObjects) {
    if (!cadObject.plyBlob) {
      if (cadObject.mesh) {
        cadObject.mesh.visible = true;
      }
      continue;
    }

    disposeMesh(cadObject.mesh);

    const mesh = await buildMesh(cadObject.plyBlob);
    mesh.visible = true;
    cadObject.mesh = mesh;
    applyNewTopoFaceColors(cadObject, highlightCadObject);
  }
}

/**
 * Builds a Three.js Mesh from a PLY blob.
 *
 * Uses the standard PLYLoader for geometry (positions, normals, colors), then
 * runs a custom binary parser to extract per-element custom properties that the
 * standard loader ignores (e.g. per-face topological IDs used by face-edge and
 * DFM visualisations).
 */
export async function buildMesh(plyBlob: Blob): Promise<THREE.Mesh> {
  const buffer = await plyBlob.arrayBuffer();
  let geometry = new PLYLoader().parse(buffer);
  const properties = parsePLYProperties(buffer);

  if (geometry.getIndex()) {
    geometry = geometry.toNonIndexed();
  }

  coerceGeometryAttributesForWebGL(geometry);

  geometry.userData['faceProperties'] =
    properties.get('face') || new Map<string, Array<number>>();
  geometry.userData['edgeProperties'] =
    properties.get('edge') || new Map<string, Array<number>>();
  geometry.userData['halfedgeProperties'] =
    properties.get('halfedge') || new Map<string, Array<number>>();
  geometry.userData['vertexProperties'] =
    properties.get('vertex') || new Map<string, Array<number>>();

  if (!geometry.hasAttribute('color')) {
    const colorArray = new Float32Array(
      geometry.getAttribute('position').count * 3,
    );
    for (let i = 0; i < colorArray.length; i += 3) {
      colorArray[i] = BASE_COLOR.r;
      colorArray[i + 1] = BASE_COLOR.g;
      colorArray[i + 2] = BASE_COLOR.b;
    }
    geometry.setAttribute(
      'color',
      new THREE.Float32BufferAttribute(colorArray, 3),
    );
  }
  geometry.setAttribute(
    'originalColor',
    geometry.getAttribute('color').clone(),
  );

  const mesh = new THREE.Mesh(
    geometry,
    new THREE.MeshStandardMaterial({
      color: 0xffffff,
      vertexColors: true,
      side: THREE.DoubleSide,
    }),
  );
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * Adds an orange overlay for an explicitly selected object or, when no object
 * is selected, the faces identified by the object's new topology metadata.
 */
export function applyNewTopoFaceColors(
  cadObject: CadObject,
  highlightCadObject: CadObject | null = null,
): boolean {
  if (!cadObject.mesh) return false;

  // Remove any existing overlay first.
  const mesh = cadObject.mesh;
  const existing = mesh.children.find(
    (c) => c.userData['overlayType'] === 'new-topo-overlay',
  );
  if (existing) {
    mesh.remove(existing);
    (existing as THREE.Mesh).geometry.dispose();
    ((existing as THREE.Mesh).material as THREE.Material).dispose();
  }

  const geometry = mesh.geometry as THREE.BufferGeometry;
  const faceProps = geometry.userData['faceProperties'] as
    Map<string, number[]> | undefined;
  const posAttr = geometry.getAttribute('position') as THREE.BufferAttribute;
  const positions = posAttr.array as Float32Array;
  const vertexCount = posAttr.count;
  const faceCount = vertexCount / 3;

  // An explicit object target takes precedence over topology-based highlighting.
  let isNewFace: ((fi: number) => boolean) | null = null;
  if (highlightCadObject?.id === cadObject.id) {
    isNewFace = () => true;
  } else if (highlightCadObject === null) {
    // Fall back to faces identified by API new_topo_faces + PLY f:topo.
    const topoFaceIds = faceProps?.get('topo');
    const newTopoFaces = cadObject.new_topo_faces;
    if (topoFaceIds && Array.isArray(newTopoFaces)) {
      const newTopoSet = new Set(newTopoFaces);
      isNewFace = (fi) =>
        newTopoSet.has(topoFaceIds[Math.min(fi, topoFaceIds.length - 1)]);
    }
  }

  if (!isNewFace) return false;

  // Collect vertex positions for new faces only.
  const overlayPositions: number[] = [];
  for (let fi = 0; fi < faceCount; fi++) {
    if (!isNewFace(fi)) continue;
    const base = fi * 9;
    for (let i = 0; i < 9; i++) overlayPositions.push(positions[base + i]);
  }
  if (overlayPositions.length === 0) return false;

  const overlayGeo = new THREE.BufferGeometry();
  overlayGeo.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(overlayPositions, 3),
  );
  overlayGeo.computeVertexNormals();

  const overlayMesh = new THREE.Mesh(
    overlayGeo,
    new THREE.MeshStandardMaterial({
      color: NEW_TOPO_COLOR,
      side: THREE.DoubleSide,
    }),
  );
  overlayMesh.userData['overlayType'] = 'new-topo-overlay';
  mesh.add(overlayMesh);
  return true;
}

function disposeMesh(mesh: THREE.Mesh | null | undefined): void {
  if (!mesh) return;

  // Overlay children (face edges, new-topo highlights, etc.) own their own
  // geometry/materials and must be disposed when the parent mesh is replaced.
  for (const child of [...mesh.children]) {
    mesh.remove(child);
    if (!(child instanceof THREE.Mesh || child instanceof THREE.Line)) {
      continue;
    }
    child.geometry.dispose();
    const materials = Array.isArray(child.material)
      ? child.material
      : [child.material];
    for (const material of materials) {
      material.dispose();
    }
  }

  mesh.geometry.dispose();
  if (Array.isArray(mesh.material)) {
    for (const material of mesh.material) {
      material.dispose();
    }
    return;
  }

  mesh.material.dispose();
}

function readPLYScalar(
  view: DataView,
  type: string,
  offset: number,
  littleEndian: boolean,
): number {
  switch (type) {
    case 'char':
    case 'int8':
      return view.getInt8(offset);
    case 'uchar':
    case 'uint8':
      return view.getUint8(offset);
    case 'short':
    case 'int16':
      return view.getInt16(offset, littleEndian);
    case 'ushort':
    case 'uint16':
      return view.getUint16(offset, littleEndian);
    case 'int':
    case 'int32':
      return view.getInt32(offset, littleEndian);
    case 'uint':
    case 'uint32':
      return view.getUint32(offset, littleEndian);
    case 'float':
    case 'float32':
      return view.getFloat32(offset, littleEndian);
    case 'double':
    case 'float64':
      return view.getFloat64(offset, littleEndian);
    default:
      return 0;
  }
}

function parsePLYProperties(
  fileBuffer: ArrayBuffer,
): Map<string, Map<string, Array<number> | Array<Array<number>>>> {
  const result = new Map<
    string,
    Map<string, Array<number> | Array<Array<number>>>
  >();

  const headerText = new TextDecoder('ascii').decode(
    fileBuffer.slice(0, Math.min(8192, fileBuffer.byteLength)),
  );
  const headerEndOffset = headerText.indexOf('end_header');
  if (headerEndOffset === -1) return result;

  type PropDef = {
    name: string;
    type: string;
    isList: boolean;
    listCountType?: string;
    listItemType?: string;
  };
  type ElemDef = { name: string; count: number; props: PropDef[] };

  const elements: ElemDef[] = [];
  let currentElem: ElemDef | null = null;
  let isBinary = false;
  let isLittleEndian = true;

  for (const rawLine of headerText.slice(0, headerEndOffset).split('\n')) {
    const parts = rawLine.trim().split(/\s+/);
    switch (parts[0]) {
      case 'format':
        isBinary = parts[1] !== 'ascii';
        isLittleEndian = parts[1] === 'binary_little_endian';
        break;
      case 'element':
        currentElem = {
          name: parts[1],
          count: parseInt(parts[2], 10),
          props: [],
        };
        elements.push(currentElem);
        break;
      case 'property':
        currentElem?.props.push(
          parts[1] === 'list'
            ? {
                name: parts[4],
                type: 'list',
                isList: true,
                listCountType: parts[2],
                listItemType: parts[3],
              }
            : { name: parts[2], type: parts[1], isList: false },
        );
        break;
    }
  }

  if (!isBinary) return result;

  const view = new DataView(fileBuffer);
  let offset = headerEndOffset + 'end_header\n'.length;

  for (const elem of elements) {
    const propMap = new Map<string, Array<number> | Array<Array<number>>>();
    result.set(elem.name, propMap);
    for (const prop of elem.props) {
      propMap.set(
        prop.name,
        prop.isList
          ? new Array<number[]>(elem.count)
          : new Array<number>(elem.count),
      );
    }

    for (let i = 0; i < elem.count && offset < fileBuffer.byteLength; i++) {
      for (const prop of elem.props) {
        if (prop.isList) {
          const count = readPLYScalar(
            view,
            prop.listCountType!,
            offset,
            isLittleEndian,
          );
          offset += PLY_TYPE_SIZES[prop.listCountType!];
          const list: number[] = [];
          for (let k = 0; k < count; k++) {
            list.push(
              readPLYScalar(view, prop.listItemType!, offset, isLittleEndian),
            );
            offset += PLY_TYPE_SIZES[prop.listItemType!];
          }
          (propMap.get(prop.name) as number[][])[i] = list;
        } else {
          (propMap.get(prop.name) as number[])[i] = readPLYScalar(
            view,
            prop.type,
            offset,
            isLittleEndian,
          );
          offset += PLY_TYPE_SIZES[prop.type];
        }
      }
    }
  }

  return result;
}
